import type { Calendar13Config } from '@/lib/calendar13Types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Calendar, Moon, Sparkles, Database, Link2 } from 'lucide-react';
import { CalendarSyncCard } from '@/components/calendar/CalendarSyncCard';

type RingSyncRecord = {
  id: string;
  name: string;
  parentId?: string;
  stagesPerParent?: number;
  stages?: number;
  waveform?: string;
};

type WidgetSyncRecord = {
  id: string;
  title: string;
  syncMode: 'linked' | 'isolated' | 'fixed';
};

export interface SettingsPanelProps {
  config: Calendar13Config;
  onUpdateConfig: (updates: Partial<Calendar13Config>) => void;
  cycleDocId?: string | null;
  ringSyncRecords?: RingSyncRecord[];
  alignmentRecords?: Array<{
    sourceCycleId: string;
    targetCycleId: string;
    sourceSegments?: number;
    targetSegments?: number;
  }>;
  widgetSyncRecords?: WidgetSyncRecord[];
  className?: string;
}

const EPOCH_PRESETS = [
  { year: 1987, name: "Harmonic Convergence", description: "Traditional Dreamspell (Year 1 = 1987)" },
  { year: 2012, name: "Galactic Alignment", description: "Mayan Long Count end date" },
  { year: 1, name: "Common Era Start", description: "Year 1 = 1 CE" },
];

export function SettingsPanel({
  config,
  onUpdateConfig,
  cycleDocId,
  ringSyncRecords = [],
  alignmentRecords = [],
  widgetSyncRecords = [],
}: SettingsPanelProps) {
  const handleEpochChange = (yearStr: string) => {
    const year = parseInt(yearStr, 10);
    // Prevent year 0 (doesn't exist in Gregorian calendar) and limit range
    if (!isNaN(year) && year >= 1 && year <= 3000) {
      onUpdateConfig({ epochYear: year });
    }
  };

  return (
    <div className="space-y-4">
      {/* Year Start Mode */}
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-4 h-4" />
            Year Start Mode
          </CardTitle>
          <CardDescription>
            Choose how the galactic year begins
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={config.yearStartMode}
            onValueChange={(value) => onUpdateConfig({ yearStartMode: value as 'fixed' | 'lunar' })}
            className="space-y-3"
          >
            <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-secondary/30 transition-colors">
              <RadioGroupItem value="fixed" id="fixed" className="mt-0.5" />
              <div className="flex-1">
                <Label htmlFor="fixed" className="flex items-center gap-2 cursor-pointer">
                  <Calendar className="w-4 h-4 text-accent" />
                  <span className="font-medium">Fixed Date</span>
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Year starts July 26 (Galactic New Year)
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-secondary/30 transition-colors opacity-60">
              <RadioGroupItem value="lunar" id="lunar" className="mt-0.5" disabled />
              <div className="flex-1">
                <Label htmlFor="lunar" className="flex items-center gap-2 cursor-pointer">
                  <Moon className="w-4 h-4 text-cosmic-moon" />
                  <span className="font-medium">Lunar Conditioned</span>
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Based on lunar cycles. Coming soon!
                </p>
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Epoch Year */}
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-4 h-4" />
            Epoch Year
          </CardTitle>
          <CardDescription>
            The Gregorian year that equals Galactic Year 1
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Presets */}
          <div className="space-y-2">
            {EPOCH_PRESETS.map((preset) => (
              <button
                key={preset.year}
                onClick={() => onUpdateConfig({ epochYear: preset.year })}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  config.epochYear === preset.year 
                    ? 'bg-primary/20 border border-primary/50' 
                    : 'hover:bg-secondary/30 border border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{preset?.name ?? 'Preset'}</span>
                  <span className="text-xs text-muted-foreground">{preset?.year ?? '—'}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{preset?.description ?? ''}</p>
              </button>
            ))}
          </div>

          {/* Custom input */}
          <div className="pt-2 border-t border-border/50">
            <Label htmlFor="custom-epoch" className="text-xs text-muted-foreground">
              Custom Epoch Year (1-3000 CE)
            </Label>
            <Input
              id="custom-epoch"
              type="number"
              value={config.epochYear}
              onChange={(e) => handleEpochChange(e.target.value)}
              className="mt-1.5 bg-background/50"
              min={1}
              max={3000}
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              Current: Year {new Date().getFullYear() - config.epochYear + 1} = {new Date().getFullYear()} CE
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Database className="w-4 h-4" />
            Cloud Sync
          </CardTitle>
          <CardDescription>
            Firestore storage details for shared cycle data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Cycles collection</span>
            <span className="font-mono text-xs">cycleSets</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">Cycles document ID</span>
            <span className="font-mono text-xs break-all text-right">
              {cycleDocId ?? 'Not created yet'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            This document stores the shared cycle data including parent relationships for all viewers.
          </p>
        </CardContent>
      </Card>

      <CalendarSyncCard />

      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Link2 className="w-4 h-4" />
            Sync Records
          </CardTitle>
          <CardDescription>
            Overview of saved alignments and widget sync settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-xs text-muted-foreground">
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80">Ring alignments</p>
            {alignmentRecords.length ? (
              <div className="space-y-1">
                {alignmentRecords.map((alignment) => (
                  <div
                    key={`${alignment.sourceCycleId}-${alignment.targetCycleId}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-border/40 bg-background/40 px-2 py-1"
                  >
                    <span className="font-mono text-[11px] text-foreground/80">
                      {alignment.sourceCycleId} ↔ {alignment.targetCycleId}
                    </span>
                    <span className="text-[11px]">
                      {alignment.sourceSegments && alignment.targetSegments
                        ? `${alignment.sourceSegments}:${alignment.targetSegments}`
                        : 'auto'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px]">No ring alignments saved.</p>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80">Ring links</p>
            {ringSyncRecords.length ? (
              <div className="space-y-1">
                {ringSyncRecords.filter(r => r && r.id && r.name).map((record) => (
                  <div
                    key={record.id}
                    className="flex flex-col gap-1 rounded-md border border-border/40 bg-background/40 px-2 py-1.5"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-[11px] font-medium text-foreground/80">
                        {record.name ?? 'Unnamed'}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {record.stages ?? '—'} stages
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 text-[10px] text-muted-foreground">
                      <span>
                        {record.waveform || 'default'} wave
                      </span>
                      <span>
                        {record.parentId
                          ? `→ ${record.parentId} (${record.stagesPerParent ?? 'auto'})`
                          : 'independent'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px]">No ring links saved.</p>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80">Widget sync</p>
            {widgetSyncRecords.length ? (
              <div className="space-y-1">
                {widgetSyncRecords.map((record) => (
                  <div
                    key={record.id}
                    className="flex items-center justify-between gap-3 rounded-md border border-border/40 bg-background/40 px-2 py-1"
                  >
                    <span className="text-[11px] text-foreground/80">{record.title}</span>
                    <span className="text-[11px] uppercase">
                      {record.syncMode === 'linked' ? 'Linked' : 'Isolated'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px]">No widget sync overrides saved.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
